<template>
  <div>
    <NuxtRouteAnnouncer />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>

<script setup>
import { watch } from 'vue';
import { useUserStore } from '~/stores/user';
import { THEMES } from '~/models/user';

// Remove the blocking onMounted hook - let individual pages handle their own loading
console.log('App: Initialized - pages will handle their own data loading');

// ── Theme application ─────────────────────────────────────────
// Watch the user's selected theme and toggle a theme-<id> class on
// the <html> element. assets/css/themes.scss defines the palette
// for each class via CSS custom properties; the rest of the
// stylesheets consume those variables. Undefined theme = no class
// applied, default Cybercore palette stays.
const userStore = useUserStore();
const THEME_CLASSES = THEMES.map(t => `theme-${t.value}`);

const applyTheme = (themeId) => {
  if (typeof document === 'undefined') return; // SSR no-op
  const html = document.documentElement;
  for (const cls of THEME_CLASSES) html.classList.remove(cls);
  if (themeId) html.classList.add(`theme-${themeId}`);
};

// Initial apply (profile may already be loaded by the time app mounts).
if (process.client) {
  applyTheme(userStore.profile?.theme);
}

// React to changes — login, profile load, theme selector save.
watch(
  () => userStore.profile?.theme,
  (v) => applyTheme(v),
  { immediate: true }
);

// ── Font-size scale ───────────────────────────────────────────
// The user's Profile "Text Size" slider stores a 0–30 percent boost.
// Apply it as --cc-font-scale on <html>; assets/css/default.scss scales
// the root font-size by it, growing every rem/em based size site-wide.
const applyFontScale = (pct) => {
  if (typeof document === 'undefined') return; // SSR no-op
  const clamped = Math.min(30, Math.max(0, Number(pct) || 0));
  document.documentElement.style.setProperty('--cc-font-scale', String(1 + clamped / 100));
};

if (process.client) {
  applyFontScale(userStore.profile?.fontScale);
}

watch(
  () => userStore.profile?.fontScale,
  (v) => applyFontScale(v),
  { immediate: true }
);
</script>
